import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FormControl, Stack, TextField, Select, MenuItem, InputLabel, Button, Box
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { Container, DataGrid } from '@/components/common';
import { useUserList, useUpdateUserStatus } from '@/hooks/users.hook';
import { successToast, errorToast } from '@/common/toast';
import { LIST_VIEW_SESSION_KEYS } from '@/util/listViewSessionKeys';
import { useListViewSessionPersistence } from '@/util/useListViewSessionPersistence';
import { DateCell, UserNameCell, UserTypeCell, UserStatusCell, GroupCell } from '../components';

const MANAGERS_DEFAULT_PAGINATION = { page: 0, pageSize: 10 };
const MANAGERS_DEFAULT_FILTERS = {
  searchType: 'all',
  searchKeyword: '',
  queryOptions: { searchType: 'all', keyword: '' },
};

/**
 * 매니저 관리 페이지 컴포넌트
 * 
 * FT, 교육운영자, 관리자 목록을 조회하고 관리하는 페이지입니다.
 * 
 * 데이터 구조:
 * - groupId (int)
 * - userId (string)
 * - name(email) (string)
 * - type (string) [FT, 관리자, 교육운영자]
 * - expireDate (datetime)
 * - regDate (datetime)
 * - status (string) [active: 활성화, inactive: 비활성화, secession: 계정 잠금]
 */
const ManagersPage = () => {
  const navigate = useNavigate();

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  } = useListViewSessionPersistence(
    LIST_VIEW_SESSION_KEYS.managers,
    MANAGERS_DEFAULT_PAGINATION,
    MANAGERS_DEFAULT_FILTERS
  );

  const searchType = filters.searchType ?? 'all';
  const searchKeyword = filters.searchKeyword ?? '';
  const queryOptions = filters.queryOptions ?? MANAGERS_DEFAULT_FILTERS.queryOptions;

  const setSearchType = useCallback((v) => {
    setFilters((f) => ({ ...f, searchType: typeof v === 'function' ? v(f.searchType) : v }));
  }, [setFilters]);
  const setSearchKeyword = useCallback((v) => {
    setFilters((f) => ({ ...f, searchKeyword: typeof v === 'function' ? v(f.searchKeyword) : v }));
  }, [setFilters]);
  const setQueryOptions = useCallback((updater) => {
    setFilters((f) => ({
      ...f,
      queryOptions: typeof updater === 'function' ? updater(f.queryOptions) : updater,
    }));
  }, [setFilters]);

  // 상태 변경 훅
  const updateStatusMutation = useUpdateUserStatus();
  const { data, isLoading } = useUserList({
    page: paginationModel.page + 1,
    size: paginationModel.pageSize,
    isStudent: false, // 매니저는 교육생이 아님
    searchType: queryOptions.searchType,
    keyword: queryOptions.keyword,
  });

  const total = data?.total || 0;
  const rowData = data?.list || [];

  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    if (!isLoading) setRowCountState(total);
  }, [total, isLoading]);

  // 상태 변경
  const handleStatusChange = useCallback((userId, newStatus, secessionOpinion) => {
    updateStatusMutation.mutate(
      { userId, status: newStatus, secessionOpinion },
      {
        onSuccess: () => successToast('매니저 상태가 변경되었습니다.'),
        onError: () => errorToast('상태 변경 실패'),
      }
    );
  }, [updateStatusMutation]);

  // 검색 실행
  const handleSearch = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setQueryOptions({
      searchType: searchType,
      keyword: searchKeyword,
    });
  }, [searchType, searchKeyword, setPaginationModel, setQueryOptions]);

  // 초기화
  const handleReset = useCallback(() => {
    resetListSession();
  }, [resetListSession]);

  // 행 클릭 핸들러
  const handleRowClick = useCallback((params) => {
    navigate(`/managers/detail/${params.row.userId}`);
  }, [navigate]);

  // 셀 클릭 핸들러 (특정 셀에서는 행 클릭 방지)
  const handleCellClick = useCallback((params, event) => {
    if (params.field === 'status') {
      event.stopPropagation();
    }
  }, []);

  // 컬럼 정의 (메모이제이션하여 불필요한 리렌더링 방지)
  const columns = useMemo(() => [
    { field: 'userId', headerName: '아이디', minWidth: 120, flex: 0.5 },
    {
      field: 'name',
      headerName: '이름 (이메일)',
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => <UserNameCell row={params.row} />,
    },
    {
      field: 'type',
      headerName: '권한',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => <UserTypeCell row={params.row} />,
    },
    {
      field: 'createdAt',
      headerName: '가입일',
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="createdAt" />,
    },
    {
      field: 'status',
      headerName: '상태',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <UserStatusCell 
          row={params.row} 
          onStatusChange={handleStatusChange}
        />
      ),
    }
  ], [handleStatusChange]);

  return (
    <Container title='매니저 관리'>
      {/* 1. 상단 필터 및 버튼 영역 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>

        {/* 검색 필터 그룹 */}
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl size='small' sx={{ width: 120 }}>
            <InputLabel>검색 조건</InputLabel>
            <Select
              value={searchType}
              label="검색 조건"
              onChange={(e) => setSearchType(e.target.value)}
            >
              <MenuItem value='all'>전체</MenuItem>
              <MenuItem value='userId'>아이디</MenuItem>
              <MenuItem value='name'>이름</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size='small'
            placeholder='검색어 입력'
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 200 }}
          />

          <Button variant='contained' onClick={handleSearch} sx={{ height: 40 }}>검색</Button>
          <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
        </Stack>

        {/* 우측 액션 버튼 그룹 */}
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/managers/new')}
          >
            매니저 등록
          </Button>
        </Stack>
      </Stack>

      {/* 2. 데이터 그리드 */}
      <DataGrid
        rows={rowData}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        height={'auto'}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row) => row.userId}
        onRowClick={handleRowClick}
        onCellClick={handleCellClick}
      />
    </Container>
  );
};

export default ManagersPage;
